<template>
  <!-- 앱 내 알림함 (2026-07-12): 기본 안읽음만 표시 + 새 알림 실시간 토스트 -->
  <el-popover placement="bottom-end" :width="340" trigger="click" popper-class="noti-popper" @show="fetchList">
    <template #reference>
      <span class="bell-wrap" title="알림">
        <el-badge :value="unread" :hidden="!unread" :max="99">
          <el-icon :size="20" color="#fff"><Bell /></el-icon>
        </el-badge>
      </span>
    </template>

    <div class="noti-head">
      <span class="noti-head-title">알림</span>
      <div class="noti-head-actions">
        <el-radio-group v-model="viewMode" size="small" @change="fetchList">
          <el-radio-button value="unread">안읽음</el-radio-button>
          <el-radio-button value="all">전체</el-radio-button>
        </el-radio-group>
        <el-button v-if="unread" link type="primary" size="small" @click="readAll">모두 읽음</el-button>
      </div>
    </div>
    <div class="noti-list">
      <div v-if="!items.length" class="noti-empty">
        {{ viewMode === 'unread' ? '안 읽은 알림이 없습니다.' : '알림이 없습니다.' }}
      </div>
      <div
        v-for="n in items"
        :key="n.id"
        class="noti-item"
        :class="{ 'is-unread': !n.is_read }"
        @click="open(n)"
      >
        <div class="noti-title">
          <el-tag v-if="n.priority === 'TOP'" size="small" type="danger" effect="plain" class="noti-pri">긴급</el-tag>
          {{ n.title }}
        </div>
        <div class="noti-body">{{ n.body }}</div>
        <div class="noti-foot">
          <span class="noti-time">{{ (n.created_at || '').slice(0, 16).replace('T', ' ') }}</span>
          <!-- 규칙 알림만 보류 가능 — '의도된 지연'(납기 연기 합의 등) 마킹 (2026-07-20) -->
          <el-dropdown v-if="n.rule_id" size="small" trigger="click"
                       @command="(d) => snooze(n, d)" @click.stop>
            <span class="noti-snooze" @click.stop>보류 ▾</span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item :command="3">3일 보류</el-dropdown-item>
                <el-dropdown-item :command="7">7일 보류</el-dropdown-item>
                <el-dropdown-item :command="30">30일 보류</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>
  </el-popover>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Bell } from '@element-plus/icons-vue'
import { ElNotification, ElMessageBox, ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import axiosInstance from '@/api/axios'

const router = useRouter()
const unread = ref(0)
const items = ref([])
const viewMode = ref('unread') // 기본: 안읽음만
let timer = null
let lastMaxId = null // 토스트 중복 방지 — 이 id 이후만 새 알림으로 간주

const fetchUnread = async () => {
  const r = await axiosInstance.get('/notifications/', { params: { unread: 1 } })
  return r.data.results || r.data || []
}

const fetchList = async () => {
  try {
    if (viewMode.value === 'unread') {
      items.value = await fetchUnread()
    } else {
      const r = await axiosInstance.get('/notifications/')
      items.value = r.data.results || r.data || []
    }
  } catch (e) { /* ignore */ }
}

// 30초 폴링: 배지 갱신 + 새 알림이면 화면 우측 토스트 (웹 켜둔 동안 실시간 인지)
const poll = async ({ toast = true } = {}) => {
  try {
    const cntRes = await axiosInstance.get('/notifications/unread-count/')
    unread.value = cntRes.data.count || 0
    if (!unread.value) {
      if (lastMaxId === null) lastMaxId = 0 // 기준점 초기화 — 이후 도착분부터 토스트
      return
    }

    const fresh = await fetchUnread()
    const isFirst = lastMaxId === null
    const newOnes = isFirst ? [] : fresh.filter((n) => n.id > lastMaxId)
    lastMaxId = Math.max(lastMaxId ?? 0, ...fresh.map((n) => n.id), 0)

    if (toast && !isFirst && newOnes.length) {
      // 최신 3건까지만 토스트 (폭주 방지)
      for (const n of newOnes.slice(0, 3)) {
        ElNotification({
          title: n.title,
          message: n.body,
          type: 'info',
          duration: 6000,
          onClick: () => open(n),
        })
      }
    }
  } catch (e) { /* 미로그인 등 — 조용히 무시 */ }
}

const open = async (n) => {
  if (!n.is_read) {
    try {
      await axiosInstance.post(`/notifications/${n.id}/read/`)
      n.is_read = true
      unread.value = Math.max(0, unread.value - 1)
      if (viewMode.value === 'unread') {
        items.value = items.value.filter((x) => x.id !== n.id)
      }
    } catch (e) { /* ignore */ }
  }
  if (n.url) router.push(n.url)
}

// 규칙 알림 보류 — 사유를 남기면 보류 기간 동안 같은 건의 재알림이 멎는다
const snooze = async (n, days) => {
  let reason = ''
  try {
    const r = await ElMessageBox.prompt(
      `${days}일 동안 이 알림을 보류합니다. 사유를 남겨주세요 (예: 납기 연기 합의).`,
      '알림 보류', { confirmButtonText: '보류', cancelButtonText: '취소',
                     inputPlaceholder: '보류 사유' })
    reason = r.value || ''
  } catch { return } // 취소
  try {
    await axiosInstance.post(`/notifications/${n.id}/snooze/`, { days, reason })
    if (!n.is_read) unread.value = Math.max(0, unread.value - 1)
    items.value = items.value.filter((x) => x.id !== n.id)
    ElMessage.success(`${days}일 보류되었습니다.`)
  } catch (e) {
    ElMessage.error(e.response?.data?.error || '보류 처리에 실패했습니다.')
  }
}

const readAll = async () => {
  try {
    await axiosInstance.post('/notifications/read-all/')
    if (viewMode.value === 'unread') items.value = []
    else items.value.forEach((n) => { n.is_read = true })
    unread.value = 0
  } catch (e) { /* ignore */ }
}

onMounted(async () => {
  await poll({ toast: false }) // 최초 로드는 기준점만 잡고 토스트 생략
  timer = setInterval(poll, 30000)
})
onBeforeUnmount(() => { if (timer) clearInterval(timer) })
</script>

<style scoped>
.bell-wrap {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 6px;
}
.noti-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;
}
.noti-head-title { font-weight: 600; }
.noti-head-actions { display: flex; align-items: center; gap: 6px; }
.noti-list { max-height: 380px; overflow-y: auto; }
.noti-empty { text-align: center; color: #909399; padding: 24px 0; font-size: 13px; }
.noti-item {
  padding: 8px 4px;
  border-bottom: 1px solid #f5f7fa;
  cursor: pointer;
}
.noti-item:hover { background: #f5f7fa; }
.noti-item.is-unread .noti-title { font-weight: 700; color: #303133; }
.noti-item.is-unread { border-left: 3px solid #409eff; padding-left: 6px; }
.noti-title { font-size: 13px; color: #606266; }
.noti-body { font-size: 12px; color: #909399; margin-top: 2px; word-break: break-all; }
.noti-time { font-size: 11px; color: #c0c4cc; }
.noti-pri { margin-right: 4px; }
.noti-foot { display: flex; justify-content: space-between; align-items: center; margin-top: 2px; }
.noti-snooze { font-size: 11px; color: #909399; cursor: pointer; }
.noti-snooze:hover { color: #409eff; }
</style>
